import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Button, Card, Empty, ErrorNote, Input, Spinner, Status } from "../components/ui";
import { relative } from "../lib/format";

export default function Projects() {
  const [q, setQ] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["projects", q],
    queryFn: () => api.projects.list({ q, limit: 50 }),
  });

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Projects</h1>
          <p className="mt-0.5 text-sm text-muted">
            {data ? `${data.total} project${data.total === 1 ? "" : "s"}` : " "}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-48">
            <Input value={q} onChange={setQ} placeholder="Search…" />
          </div>
          <Link to="/app/new">
            <Button>New project</Button>
          </Link>
        </div>
      </header>

      {error && <ErrorNote error={error} />}
      {isLoading && <Spinner />}

      {!isLoading && data && data.items.length === 0 && (
        <Empty
          title={q ? "No matches" : "Nothing here yet"}
          hint={
            q
              ? "No project matches that search."
              : "Describe what you want built and Atelier will design the data model, generate the code, and deploy it."
          }
          action={
            !q && (
              <Link to="/app/new">
                <Button>Build something</Button>
              </Link>
            )
          }
        />
      )}

      <div className="grid gap-3">
        {data?.items.map((p) => (
          <Link key={p.id} to={`/app/projects/${p.id}`}>
            <Card className="p-4 transition hover:border-clay/60">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-sm font-medium">{p.name}</h2>
                    <Status value={p.status} />
                  </div>
                  <p className="mt-1 line-clamp-2 max-w-2xl text-xs leading-relaxed text-muted">{p.prompt}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {p.surfaces.split(",").filter(Boolean).map((s) => (
                      <span key={s} className="rounded-full border border-hairline px-2 py-0.5 text-2xs text-muted">
                        {s}
                      </span>
                    ))}
                    {p.features.split(",").filter(Boolean).map((f) => (
                      <span key={f} className="rounded-full border border-clay/30 px-2 py-0.5 text-2xs text-clay">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  {p.app_url && p.status === "live" ? (
                    <span className="block max-w-[16rem] truncate font-mono text-2xs text-moss">
                      {p.app_url.replace(/^https?:\/\//, "")}
                    </span>
                  ) : null}
                  <span className="mt-1 block text-2xs text-muted">{relative(p.updated_at)}</span>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
